import { RefreshCw } from "@/components/ui/icons";
import { Animated, Pressable, Text, View } from "react-native";

import type { useCollapsingHeader } from "@/hooks/useCollapsingHeader";
import { useThemeColors } from "@/theme/theme";

/** How old stored prices may be before the storefront says when they are from (Q2). */
const STALE_AFTER_MS = 36 * 60 * 60 * 1000;

/** The "Prices as of" date, or null while the prices are fresh, unknown or hidden. */
export function stalePricesDate(pricedAsOf: string | null, now: Date = new Date()): string | null {
  if (!pricedAsOf) return null;
  const at = Date.parse(pricedAsOf);
  if (Number.isNaN(at) || now.getTime() - at <= STALE_AFTER_MS) return null;
  return new Date(at).toLocaleDateString([], { month: "short", day: "numeric" });
}

/**
 * A quiet line above the cards saying when the stored prices were quoted (Q2), shown only once
 * they are more than 36 hours old. A shop that shows no prices sends no date, so it shows nothing.
 */
export function PricesAsOfLine({ pricedAsOf, now }: { pricedAsOf: string | null; now?: Date; }) {
  const date = stalePricesDate(pricedAsOf, now);
  if (!date) return null;
  return (
    <Text testID="prices-as-of" className="pb-2 text-xs font-body text-muted-foreground">
      Prices as of {date}
    </Text>
  );
}

/**
 * The "Prices updated" pill (Q12): a later page reported prices newer than the first page's, so
 * the cards above may show old ones. Tapping it reloads from the top. Announced politely, since it
 * appears without the shopper doing anything.
 */
export function PricesUpdatedPill({ onPress }: { onPress: () => void; }) {
  const c = useThemeColors();
  return (
    <View
      className="items-center pt-2"
      pointerEvents="box-none"
      accessibilityLiveRegion="polite"
      aria-live="polite"
    >
      <Pressable
        testID="prices-updated-pill"
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel="Prices updated. Tap to refresh"
        className="min-h-11 flex-row items-center gap-1.5 rounded-full bg-primary px-4"
      >
        <RefreshCw size={14} color={c.primaryForeground} aria-hidden />
        <Text className="font-label text-sm text-primary-foreground">
          Prices updated · Tap to refresh
        </Text>
      </Pressable>
    </View>
  );
}

/**
 * The pill pinned at the top of the results, just under a storefront's floating header. A sibling
 * of the list rather than a child hanging off the header, because a native view never receives a
 * touch outside its parent's frame; it borrows the header's slide so it follows the header's edge
 * as the identity scrolls away.
 */
export function PricesUpdatedOverlay({ headerHeight, headerStyle, onPress }: {
  headerHeight: number;
  headerStyle: Pick<ReturnType<typeof useCollapsingHeader>["header"]["style"], "transform">;
  onPress: () => void;
}) {
  return (
    <Animated.View
      pointerEvents="box-none"
      style={{
        position: "absolute",
        top: headerHeight,
        left: 0,
        right: 0,
        zIndex: 10,
        transform: headerStyle.transform,
      }}
    >
      <PricesUpdatedPill onPress={onPress} />
    </Animated.View>
  );
}
